import { Link } from '@tanstack/react-router';
import { LazyMotion, m } from 'motion/react';
import { domAnimation } from '@/lib/motionFeatures';
import { useRef } from 'react';
import { PAGE_IDS } from '@/constants/pages';
import { useSamePageNavigation } from '@/hooks/useSamePageNavigation';
import { tw } from '@/lib/tailwind';
import { DashboardNavigationItem } from '@/types';
import { ChevronDown } from '@alchemer/ui/icons';
import { useFlag } from '@alchemer/unleash';
import { FEATURE_FLAGS } from '@/config/features';

type SubNavProps = {
  page: string;
  subnav: DashboardNavigationItem[];
  isSubNavOpen: boolean;
  setIsSubNavOpen: (isOpen: boolean) => void;
};

const MotionIcon = m.create(ChevronDown);

export default function SubNav({ subnav, page, isSubNavOpen, setIsSubNavOpen }: SubNavProps) {
  const subNavRef = useRef<HTMLElement | null>(null);
  const { isCurrentPage } = useSamePageNavigation();
  const isDatasetsV2 = useFlag(FEATURE_FLAGS.DATASETS_2_0);
  const isLabelManagementEnabled = useFlag(FEATURE_FLAGS.LABEL_MANAGEMENT);

  return (
    <LazyMotion features={domAnimation}>
      <div className="flex items-center">
        <button
          id="btnSubNav"
          aria-haspopup="menu"
          aria-label={`${page} sub navigation`}
          className={tw(
            `flex items-center justify-center`,
            `round-xs border-none outline-none`,
            `text-muted-foreground`,
            `group-hover:text-accent-foreground group-hover:bg-background-100 transition-colors`,
            `h-[1em] w-[1em] cursor-pointer p-0`,
          )}
          onClick={() => setIsSubNavOpen(!isSubNavOpen)}
        >
          <MotionIcon
            animate={{ rotate: isSubNavOpen ? -90 : 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          />
        </button>
        <menu
          id={`${page}-subnav`}
          ref={subNavRef}
          className={tw(
            `m-0 min-w-45 bg-white p-1`,
            `absolute top-[120%] left-0 flex-col`,
            `border-border rounded-sm border border-solid shadow-lg`,
            `transition-all duration-200`,
            isSubNavOpen && 'visible opacity-100',
            !isSubNavOpen && 'invisible opacity-0',
          )}
        >
          {subnav.map((item) => {
            if (item.pageId === PAGE_IDS.Sources && isDatasetsV2) return null;
            if (item.pageId === PAGE_IDS.QuestionLabels && !isLabelManagementEnabled) return null;

            let linkTo = '';

            if (item.pageId === PAGE_IDS.Schedules) {
              linkTo = `/dashboard/schedules`;
            } else if (item.pageId === PAGE_IDS.Alerts || item.pageId === PAGE_IDS.Subscriptions) {
              linkTo = `/dashboard/$page`;
            } else if (item.pageId === PAGE_IDS.Datasets) {
              linkTo = isDatasetsV2 ? `/dashboard/${PAGE_IDS.Datasets}` : `/dashboard/data-source/${PAGE_IDS.Datasets}`;
            } else if (item.pageId === PAGE_IDS.Sources) {
              linkTo = `/dashboard/data-source/${PAGE_IDS.Sources}`;
            } else if (item.pageId === PAGE_IDS.QuestionLabels) {
              linkTo = `/dashboard/question-labels`;
            }

            return (
              <Link
                key={item.pageId}
                to={linkTo}
                params={{
                  page: item.pageId,
                }}
                search={isCurrentPage(linkTo, { page: item.pageId }) ? { reload: new Date().getTime() } : {}}
                onClick={() => setIsSubNavOpen(false)}
                preload={item.preload}
              >
                <div className={tw('hover:bg-background-200 cursor-pointer rounded-xs', 'p-[0.5em] whitespace-nowrap')}>
                  {item.label}
                </div>
              </Link>
            );
          })}
        </menu>
      </div>
    </LazyMotion>
  );
}
